import { ApolloLink, HttpLink, concat, split } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { getJwtToken, getUuid } from "@biggeo/bg-common";
import {
    Applications,
    SubscriptionData,
    SubscriptionGeometry,
    SubscriptionResponse,
} from "@biggeo/bg-server-lib/studio";
import {
    bigIntToBgLong,
    getVelocityUrlOnLocal,
    longToBgLong,
    longToNumber,
} from "@biggeo/bg-utils";
import { createClient } from "graphql-ws";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import { defaultLevelSets } from "../../constants/map.constants";
import { studioUrlFromEnv } from "../../config/env.config";

import { io } from "socket.io-client";
import { SpatialDataRsp } from "../../velocity/aurora/spatial";
import { CreateSocket } from "./type";

const serverLocation = window.location.host;
const serverPort =
    window.location.port ||
    (window.location.protocol === "https:" ? "443" : "80");
const serverProtocol = window.location.protocol;
const websocketProtocol = serverProtocol === "https:" ? "wss:" : "ws:";

const getServerHost = () =>
    serverProtocol === "https:" ? serverLocation : "localhost";

const getServerPort = () => {
    if (!serverPort) {
        return serverProtocol === "https:" ? "443" : "80";
    }
    return serverPort;
};

export const getServerEndpoint = () => {
    const studio_server_url = studioUrlFromEnv();
    if (studio_server_url) return studio_server_url;
    return `${serverProtocol}//${getServerHost()}:${getServerPort()}`;
};

export const getWsEndpoint = () => {
    const studio_server_url = studioUrlFromEnv();
    if (studio_server_url) return studio_server_url;
    return `${websocketProtocol}//${getServerHost()}:${getServerPort()}`;
};

export const nodeWsLink = new GraphQLWsLink(
    createClient({ url: `${getWsEndpoint()}/api/sub` })
);

export const httpLink = new HttpLink({ uri: `${getServerEndpoint()}/api` });

export const appHttpLink = new HttpLink({ uri: `${getServerEndpoint()}` });

const authMiddleware = ({
    headers,
    app,
}: {
    headers: Record<string, string | undefined>;
    app: Applications;
}) =>
    new ApolloLink((operation, forward) => {
        const token = getJwtToken(app);
        const uuid = getUuid(app);

        // add the authorization to the headers

        operation.setContext({
            headers: {
                uuid,
                ...(token ? { authorization: `Bearer ${token}` } : {}),
                ...headers,
            },
        });
        return forward(operation);
    });

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
        );
    },
    nodeWsLink,
    httpLink
);

export const getSplitLink = ({
    headers,
    app,
}: {
    readonly headers: Record<string, string | undefined>;
    readonly app: Applications;
}) => {
    return concat(authMiddleware({ headers, app }), splitLink);
};

export const convertSocketStringResponseToSubscriptionResponse = ({
    searchResult: data,
    timeMs,
    ...props
}: SpatialDataRsp): SubscriptionResponse => {
    if (!data) {
        return {
            databaseId: props.databaseId,
            dateTime: props.dateTime,
            requestId: props.requestId,
            uniqueId: props.uniqueId,
            collectionExist: props.collectionExist,
        };
    }

    return {
        ...props,
        geometry: {
            aggregation:
                compact(
                    data.aggregates.map((agg) => {
                        if (!agg.cellIndex) return;
                        const data: SubscriptionData = {
                            cellID: agg.cellIndex?.index,
                            count: longToNumber(agg.pointCount),
                            cellVertices: {
                                points:
                                    agg.cellVertices?.points.map((p) => {
                                        return {
                                            latitude: p?.latitude || 0,
                                            longitude: p?.longitude || 0,
                                        };
                                    }) || [],
                            },
                            meanPoint: {
                                latitude: agg?.meanPoint?.latitude || 0,
                                longitude: agg?.meanPoint?.longitude || 0,
                            },
                        };
                        return data;
                    })
                ) || [],
            points: compact(
                data.records.map((p): SubscriptionGeometry | undefined => {
                    if (p.geometry?.point && p.id) {
                        return {
                            id: p.id.uint64
                                ? {
                                      uint64: longToBgLong(p.id.uint64),
                                  }
                                : { string: p.id?.string },
                            point: {
                                latitude: p.geometry.point.latitude || 0,
                                longitude: p.geometry.point.longitude || 0,
                            },
                        };
                    }
                })
            ),

            // TODO: This is a temporary fix and is unnecessary with a fix currently in bgsearch, but not yet released
            levelSets: isEmpty(data.levelSets)
                ? defaultLevelSets
                : data.levelSets,
            timeMs: longToNumber(timeMs),
            pointCount: bigIntToBgLong(
                BigInt(
                    data.records.reduce(
                        (p, c) => (c.geometry?.point ? p + 1 : p),
                        0
                    ) || 0
                )
            ),
            nonPointCount: bigIntToBgLong(
                BigInt(
                    data.aggregates?.reduce(
                        (p, c) => p + (longToNumber(c.nonPointCount) || 0),
                        0
                    ) || 0
                )
            ),
            nonPoints: data.records
                ?.flatMap((g) => {
                    if (
                        g.geometry?.polygon ||
                        g.geometry?.multiPolygon ||
                        g.geometry?.lineStrip
                    ) {
                        return {
                            id: g.id,
                            polygon: g.geometry.polygon,
                            multipolygon: g.geometry.multiPolygon,
                            lineStrip: g.geometry.lineStrip,
                        };
                    }
                })
                .filter((p) => p !== undefined) as SubscriptionGeometry[],
        },
    };
};

// ██╗   ██╗███████╗██╗      ██████╗  ██████╗██╗████████╗██╗   ██╗    ███████╗ ██████╗  ██████╗██╗  ██╗███████╗████████╗███████╗
// ██║   ██║██╔════╝██║     ██╔═══██╗██╔════╝██║╚══██╔══╝╚██╗ ██╔╝    ██╔════╝██╔═══██╗██╔════╝██║ ██╔╝██╔════╝╚══██╔══╝██╔════╝
// ██║   ██║█████╗  ██║     ██║   ██║██║     ██║   ██║    ╚████╔╝     ███████╗██║   ██║██║     █████╔╝ █████╗     ██║   ███████╗
// ╚██╗ ██╔╝██╔══╝  ██║     ██║   ██║██║     ██║   ██║     ╚██╔╝      ╚════██║██║   ██║██║     ██╔═██╗ ██╔══╝     ██║   ╚════██║
//  ╚████╔╝ ███████╗███████╗╚██████╔╝╚██████╗██║   ██║      ██║       ███████║╚██████╔╝╚██████╗██║  ██╗███████╗   ██║   ███████║
//   ╚═══╝  ╚══════╝╚══════╝ ╚═════╝  ╚═════╝╚═╝   ╚═╝      ╚═╝       ╚══════╝ ╚═════╝  ╚═════╝╚═╝  ╚═╝╚══════╝   ╚═╝   ╚══════╝

export const createSocket = ({
    serverUrl,
    crate,
    path,
    userJwt,
}: CreateSocket) => {
    const isDevelopment = import.meta.env.MODE === "development";

    const url = isDevelopment ? getVelocityUrlOnLocal(serverUrl) : serverUrl;

    return io(`${url}/${crate}`, {
        path,
        transports: ["websocket"],
        query: {
            token: userJwt,
        },
    });
};
